/**
 * @description
 * Organization overview tab component showing organization info and analytics.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-06
 */

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import type { OrganizationOverviewProps } from "./types";

const CHART_COLORS = [
  "#8884d8",
  "#82ca9d",
  "#ffc658",
  "#ff7c7c",
  "#8dd1e1",
  "#d084d0",
  "#ffb347",
];

export function OrganizationOverview({
  organization,
  workspaces,
  apiKeys,
  usageData,
  loading: _loading,
  onRefresh,
}: OrganizationOverviewProps) {
  const calculateTotalTokens = () => {
    let uncached = 0;
    let cacheCreation1h = 0;
    let cacheCreation5m = 0;
    let cacheRead = 0;
    let output = 0;

    if (!usageData || !Array.isArray(usageData)) {
      return { uncached, cacheCreation1h, cacheCreation5m, cacheRead, output };
    }

    usageData.forEach((bucket) => {
      bucket.results.forEach((result) => {
        uncached += result.uncached_input_tokens;
        cacheCreation1h += result.cache_creation.ephemeral_1h_input_tokens;
        cacheCreation5m += result.cache_creation.ephemeral_5m_input_tokens;
        cacheRead += result.cache_read_input_tokens;
        output += result.output_tokens;
      });
    });

    return { uncached, cacheCreation1h, cacheCreation5m, cacheRead, output };
  };

  const getWorkspaceUsageData = () => {
    const workspaceMap = new Map<string, number>();
    
    if (!usageData || !Array.isArray(usageData)) {
      return [];
    }
    
    usageData.forEach((bucket) => {
      bucket.results.forEach((result) => {
        const wsName = result.workspace_id || "N/A";
        const total =
          result.uncached_input_tokens +
          result.cache_read_input_tokens +
          result.output_tokens;
        workspaceMap.set(wsName, (workspaceMap.get(wsName) || 0) + total);
      });
    });
    return Array.from(workspaceMap.entries()).map(([name, value]) => ({
      name,
      value,
    }));
  };

  const getModelUsageData = () => {
    const modelMap = new Map<string, number>();
    
    if (!usageData || !Array.isArray(usageData)) {
      return [];
    }
    
    usageData.forEach((bucket) => {
      bucket.results.forEach((result) => {
        const model = result.model || "Unknown";
        const total =
          result.uncached_input_tokens +
          result.cache_read_input_tokens +
          result.output_tokens;
        modelMap.set(model, (modelMap.get(model) || 0) + total);
      });
    });
    return Array.from(modelMap.entries()).map(([name, value]) => ({
      name,
      value,
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Organization Overview</h2>
        <button
          onClick={onRefresh}
          className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent"
        >
          Refresh
        </button>
      </div>

      {organization && (
        <>
          {/* Organization Info */}
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-md border p-4">
              <label className="text-sm font-medium text-muted-foreground">
                Organization Name
              </label>
              <p className="mt-1 text-xl font-semibold">
                {organization.name}
              </p>
            </div>
            <div className="rounded-md border p-4">
              <label className="text-sm font-medium text-muted-foreground">
                Total Workspaces
              </label>
              <p className="mt-1 text-xl font-semibold">
                {workspaces.length}
              </p>
            </div>
            <div className="rounded-md border p-4">
              <label className="text-sm font-medium text-muted-foreground">
                Active API Keys
              </label>
              <p className="mt-1 text-xl font-semibold">
                {apiKeys.filter((k) => k.status === "active").length}
              </p>
            </div>
          </div>

          {/* Analytics Charts */}
          {usageData && Array.isArray(usageData) && usageData.length > 0 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold">
                Usage Analytics (Last 7 Days)
              </h3>

              {/* Usage Overview */}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-md border p-4">
                  <h4 className="mb-4 font-medium">
                    Total Tokens by Workspace
                  </h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={getWorkspaceUsageData()}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="name"
                        angle={-45}
                        textAnchor="end"
                        height={100}
                      />
                      <YAxis />
                      <Tooltip
                        formatter={(value) => value.toLocaleString()}
                      />
                      <Bar dataKey="value" fill="#8884d8" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="rounded-md border p-4">
                  <h4 className="mb-4 font-medium">Token Usage by Model</h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={getModelUsageData()}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) =>
                          `${name}: ${((percent as number) * 100).toFixed(0)}%`
                        }
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {getModelUsageData().map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={CHART_COLORS[index % CHART_COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => value.toLocaleString()}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Summary Stats */}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-md border p-4">
                  <p className="text-sm font-medium text-muted-foreground">
                    Total Tokens
                  </p>
                  <p className="mt-2 text-2xl font-bold">
                    {(
                      calculateTotalTokens().uncached +
                      calculateTotalTokens().cacheRead +
                      calculateTotalTokens().output
                    ).toLocaleString()}
                  </p>
                </div>
                <div className="rounded-md border p-4">
                  <p className="text-sm font-medium text-muted-foreground">
                    Cache Hit Rate
                  </p>
                  <p className="mt-2 text-2xl font-bold">
                    {(
                      (calculateTotalTokens().cacheRead /
                        (calculateTotalTokens().uncached +
                          calculateTotalTokens().cacheRead || 1)) *
                      100
                    ).toFixed(1)}
                    %
                  </p>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
